/**
 * AdminSubscriptions — thin table view over GET /admin/subscription-table.
 *
 *   • Filters: All / Active / Complimentary / Unsubscribed / Admin Override
 *   • Per-row: Override · Remove Override · View Audit
 *   • Override modal: tier dropdown + reason (required) + optional expiry
 *
 * Endpoints used (all already live):
 *   GET    /api/admin/subscription-table
 *   POST   /api/admin/practitioners/{id}/subscription-override
 *   DELETE /api/admin/practitioners/{id}/subscription-override
 *   GET    /api/admin/audit-log?target_email=…
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import AdminShell from "@/components/AdminShell";
import { adminAuth } from "@/lib/api";
import {
  Loader2, Shield, RefreshCcw, X, Sparkles, ClipboardList, Trash2, Receipt, UserPlus,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = [
  { key: "all",            label: "All" },
  { key: "active",         label: "Active" },
  { key: "complimentary",  label: "Complimentary" },
  { key: "unsubscribed",   label: "Unsubscribed" },
  { key: "admin_override", label: "Admin Override" },
];

const TIER_BADGE = {
  lite:       "bg-blue-100   text-blue-800",
  pro:        "bg-purple-100 text-purple-900",
  enterprise: "bg-amber-100  text-amber-900",   // gold-toned amber
  none:       "bg-slate-100  text-slate-600",
};
const STATUS_BADGE = {
  active:         "bg-emerald-100 text-emerald-800",
  complimentary:  "bg-purple-100  text-purple-900",
  unsubscribed:   "bg-amber-100   text-amber-900",
  past_due:       "bg-rose-100    text-rose-800",
  admin_override: "bg-purple-100  text-purple-900",
  cancelled:      "bg-slate-100   text-slate-500",
};

const auth = () => ({ headers: { Authorization: `Bearer ${adminAuth.getToken()}` } });

export default function AdminSubscriptions() {
  const [filter, setFilter] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [overrideTarget, setOverrideTarget] = useState(null);
  const [auditTarget, setAuditTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/subscription-table`, {
        ...auth(),
        params: filter === "all" ? {} : { status: filter },
      });
      setRows(r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Could not load subscriptions");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const removeOverride = async (row) => {
    if (!confirm(`Remove override for ${row.email}? Their tier will revert to their underlying subscription state.`)) return;
    try {
      await axios.delete(`${API}/admin/practitioners/${row.id}/subscription-override`, auth());
      toast.success("Override removed");
      load();
    } catch (e) { toast.error("Could not remove override"); }
  };

  return (
    <AdminShell door="university" title="Subscriptions">
      <div data-testid="admin-subscriptions-page" className="space-y-4">
        <header className="flex items-center justify-between flex-wrap gap-2">
          <div className="inline-flex items-center gap-2 text-purple-900">
            <Shield className="w-4 h-4" />
            <span className="text-sm font-medium">Subscription Management</span>
          </div>
          <button onClick={load} className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1" data-testid="subs-refresh">
            <RefreshCcw className="w-3 h-3" />Refresh
          </button>
        </header>

        <div className="flex items-center gap-2 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                filter === f.key
                  ? "border-purple-900 bg-purple-50 text-purple-900"
                  : "border-slate-200 text-slate-600 hover:border-purple-300"
              }`}
              data-testid={`subs-filter-${f.key}`}
            >{f.label}</button>
          ))}
        </div>

        <section className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Name</th>
                <th className="text-left px-3 py-2 font-medium">Email</th>
                <th className="text-left px-3 py-2 font-medium">Tier</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-left px-3 py-2 font-medium">Source</th>
                <th className="text-left px-3 py-2 font-medium">Expires / Period</th>
                <th className="text-right px-3 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400" data-testid="subs-empty">No rows match this filter.</td></tr>
              ) : rows.map((r) => {
                const expiry = r.complimentary_expires_at || r.admin_override_expires_at || r.current_period_end || "";
                return (
                  <tr key={r.id} className="border-t border-slate-100" data-testid={`subs-row-${r.id}`}>
                    <td className="px-3 py-2 font-medium text-slate-900 truncate max-w-[160px]">{r.name || "—"}</td>
                    <td className="px-3 py-2 text-slate-700 truncate max-w-[220px]" title={r.email}>{r.email}</td>
                    <td className="px-3 py-2"><span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${TIER_BADGE[r.effective_tier] || TIER_BADGE.none}`}>{r.effective_tier}</span></td>
                    <td className="px-3 py-2"><span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${STATUS_BADGE[r.subscription_status] || "bg-slate-100 text-slate-600"}`}>{r.subscription_status || "—"}</span></td>
                    <td className="px-3 py-2 text-slate-600">{r.source || "—"}</td>
                    <td className="px-3 py-2 text-slate-500 text-xs">{expiry ? new Date(expiry).toLocaleDateString() : "—"}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button onClick={() => setOverrideTarget(r)} className="text-xs text-purple-900 hover:underline mr-2" data-testid={`subs-override-${r.id}`}>Override</button>
                      {r.source === "admin_override" && (
                        <button onClick={() => removeOverride(r)} className="text-xs text-rose-700 hover:underline mr-2" data-testid={`subs-remove-${r.id}`}>
                          <Trash2 className="w-3 h-3 inline" /> Remove
                        </button>
                      )}
                      <button onClick={() => setAuditTarget(r)} className="text-xs text-slate-600 hover:underline" data-testid={`subs-audit-${r.id}`}>Audit</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>

        {overrideTarget && <OverrideModal row={overrideTarget} onClose={() => setOverrideTarget(null)} onSaved={() => { setOverrideTarget(null); load(); }} />}
        {auditTarget && <AuditModal row={auditTarget} onClose={() => setAuditTarget(null)} />}

        <BillingEventsPanel />
        <PendingSignupsPanel />
      </div>
    </AdminShell>
  );
}

// ─────────────────────────── Pending Signups Panel (Phase 4 — Door 1) ──
function PendingSignupsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("pending");

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/pending-signups`, {
        ...auth(),
        params: { ...(statusFilter ? { status: statusFilter } : {}), limit: 100 },
      });
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Could not load pending signups");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  return (
    <section className="mt-8" data-testid="subs-pending-signups-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="inline-flex items-center gap-2 text-purple-900">
          <UserPlus className="w-4 h-4" />
          <h3 className="font-display text-base">Pending Signups <span className="text-xs text-slate-500 font-sans">(Door 1 — Public checkout awaiting payment)</span></h3>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-xs rounded-xl border border-slate-200 h-8 px-2"
            data-testid="pending-signups-filter-status"
          >
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="abandoned">Abandoned (&gt; 7 days)</option>
            <option value="failed">Failed</option>
            <option value="">All</option>
          </select>
          <button onClick={load} className="text-xs text-purple-900 hover:underline inline-flex items-center gap-1" data-testid="pending-signups-refresh">
            <RefreshCcw className="w-3 h-3" />Refresh
          </button>
        </div>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <table className="min-w-full text-xs">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Created</th>
              <th className="text-left px-3 py-2 font-medium">Email</th>
              <th className="text-left px-3 py-2 font-medium">Name</th>
              <th className="text-left px-3 py-2 font-medium">Clinic</th>
              <th className="text-left px-3 py-2 font-medium">Tier</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="text-left px-3 py-2 font-medium">Invoice</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400" data-testid="pending-signups-empty">
                No {statusFilter || "pending"} signups.
              </td></tr>
            ) : items.map((s) => (
              <tr key={s.token} className="border-t border-slate-100" data-testid={`pending-signup-${s.token}`}>
                <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{(s.created_at || "").slice(0, 19).replace("T", " ")}</td>
                <td className="px-3 py-2 text-slate-800 truncate max-w-[180px]" title={s.email}>{s.email}</td>
                <td className="px-3 py-2 text-slate-700 truncate max-w-[140px]" title={s.full_name}>{s.full_name}</td>
                <td className="px-3 py-2 text-slate-500 truncate max-w-[120px]">{s.clinic_name || "—"}</td>
                <td className="px-3 py-2 text-purple-900 font-medium capitalize">{s.tier}</td>
                <td className="px-3 py-2">
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
                    s.status === "pending" ? "bg-amber-100 text-amber-900" :
                    s.status === "completed" ? "bg-emerald-100 text-emerald-900" :
                    s.status === "abandoned" ? "bg-slate-100 text-slate-600" :
                    "bg-rose-100 text-rose-900"
                  }`}>{s.status}</span>
                </td>
                <td className="px-3 py-2">
                  {s.invoice_url ? (
                    <a href={s.invoice_url} target="_blank" rel="noopener noreferrer" className="text-xs text-purple-900 hover:underline">Open →</a>
                  ) : <span className="text-slate-300">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ─────────────────────────── Billing Events Panel (Phase 3) ─────────────
function BillingEventsPanel() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterEmail, setFilterEmail] = useState("");
  const [filterType, setFilterType] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/billing-events`, {
        ...auth(),
        params: {
          ...(filterEmail ? { practitioner_email: filterEmail.trim() } : {}),
          ...(filterType ? { event_type: filterType } : {}),
          limit: 100,
        },
      });
      setEvents(r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Could not load billing events");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <section className="mt-8" data-testid="subs-billing-events-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="inline-flex items-center gap-2 text-purple-900">
          <Receipt className="w-4 h-4" />
          <h3 className="font-display text-base">Billing Events <span className="text-xs text-slate-500 font-sans">(Square webhook stream)</span></h3>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            placeholder="Filter by practitioner email"
            value={filterEmail}
            onChange={(e) => setFilterEmail(e.target.value)}
            className="text-xs rounded-xl border border-slate-200 h-8 px-3 w-56"
            data-testid="billing-events-filter-email"
          />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="text-xs rounded-xl border border-slate-200 h-8 px-2"
            data-testid="billing-events-filter-type"
          >
            <option value="">All event types</option>
            <option value="subscription.created">subscription.created</option>
            <option value="subscription.updated">subscription.updated</option>
            <option value="invoice.created">invoice.created</option>
            <option value="invoice.updated">invoice.updated</option>
            <option value="invoice.payment_made">invoice.payment_made</option>
          </select>
          <button onClick={load} className="text-xs text-purple-900 hover:underline inline-flex items-center gap-1" data-testid="billing-events-refresh">
            <RefreshCcw className="w-3 h-3" />Refresh
          </button>
        </div>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <table className="min-w-full text-xs">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Received</th>
              <th className="text-left px-3 py-2 font-medium">Event Type</th>
              <th className="text-left px-3 py-2 font-medium">Practitioner</th>
              <th className="text-left px-3 py-2 font-medium">Subscription</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="text-right px-3 py-2 font-medium">Amount</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>
            ) : events.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400" data-testid="billing-events-empty">
                No billing events yet. Once the Square webhook is wired in the Dashboard, subscription events will appear here.
              </td></tr>
            ) : events.map((e) => (
              <tr key={e.id} className="border-t border-slate-100" data-testid={`billing-event-${e.id}`}>
                <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{(e.received_at || "").slice(0, 19).replace("T", " ")}</td>
                <td className="px-3 py-2 font-mono text-purple-900">{e.type || "—"}</td>
                <td className="px-3 py-2 text-slate-700 truncate max-w-[180px]" title={e.practitioner_email}>{e.practitioner_email || "—"}</td>
                <td className="px-3 py-2 text-slate-500 font-mono">{(e.subscription_id || "—").slice(0, 14)}{e.subscription_id?.length > 14 ? "…" : ""}</td>
                <td className="px-3 py-2 text-slate-700">{e.status || "—"}</td>
                <td className="px-3 py-2 text-right text-slate-700">{e.amount_usd ? `$${Number(e.amount_usd).toFixed(2)}` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function OverrideModal({ row, onClose, onSaved }) {
  const [tier, setTier] = useState(row.effective_tier || "pro");
  const [reason, setReason] = useState("");
  const [expiry, setExpiry] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!reason.trim()) return toast.error("Reason is required");
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/practitioners/${row.id}/subscription-override`,
        {
          tier,
          reason: reason.trim(),
          expires_at: expiry ? new Date(expiry + "T23:59:59Z").toISOString() : null,
        },
        auth()
      );
      toast.success("Override applied");
      onSaved();
    } catch (e) { toast.error("Could not save override"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="subs-override-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="inline-flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-900" /><h3 className="font-display text-lg text-slate-900">Override Subscription</h3></div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-slate-500 mb-4">Target: <b>{row.email}</b></p>

        <label className="block text-[11px] uppercase tracking-wide text-slate-600 mb-1">Tier</label>
        <select className="form-input mb-3" value={tier} onChange={(e) => setTier(e.target.value)} data-testid="subs-override-tier">
          <option value="lite">Lite</option>
          <option value="pro">Pro</option>
          <option value="enterprise">Enterprise</option>
          <option value="none">None (revoke access)</option>
        </select>

        <label className="block text-[11px] uppercase tracking-wide text-slate-600 mb-1">Reason <span className="text-rose-600">*</span></label>
        <textarea className="form-input min-h-[90px] mb-3" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Beta partner — comped pending paperwork" data-testid="subs-override-reason" />

        <label className="block text-[11px] uppercase tracking-wide text-slate-600 mb-1">Expires (optional)</label>
        <input type="date" className="form-input" value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid="subs-override-expiry" />
        <div className="text-[11px] text-slate-500 mt-1">Leave blank for indefinite override.</div>

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm h-9 px-4" data-testid="subs-override-cancel">Cancel</button>
          <button onClick={save} disabled={busy} className="rounded-xl bg-purple-900 hover:bg-purple-800 text-white text-sm font-medium h-9 px-4 inline-flex items-center gap-2 disabled:opacity-60" data-testid="subs-override-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}Apply Override
          </button>
        </div>
      </div>
    </div>
  );
}

function AuditModal({ row, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/audit-log`, { ...auth(), params: { target_email: row.email, limit: 100 } })
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [row.email]);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="subs-audit-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-5 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <div className="inline-flex items-center gap-2"><ClipboardList className="w-4 h-4 text-purple-900" /><h3 className="font-display text-lg text-slate-900">Audit Log — {row.email}</h3></div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X className="w-4 h-4" /></button>
        </div>
        {loading ? (
          <div className="text-slate-500 text-sm inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading audit history…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-slate-400">No audit entries yet for this account.</div>
        ) : (
          <ol className="space-y-2 text-sm">
            {items.map((e, i) => (
              <li key={i} className="rounded-xl border border-slate-100 bg-slate-50 p-3" data-testid={`subs-audit-row-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-purple-900 text-xs">{e.action}</span>
                  <span className="text-[11px] text-slate-500">{e.timestamp ? new Date(e.timestamp).toLocaleString() : "—"}</span>
                </div>
                <div className="text-xs text-slate-600 mt-1">by {e.performed_by}</div>
                {e.details && Object.keys(e.details).length > 0 && (
                  <pre className="mt-2 text-[11px] bg-white p-2 rounded border border-slate-100 overflow-x-auto">{JSON.stringify(e.details, null, 2)}</pre>
                )}
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
